/**
 * BillWisePage — Sundry Creditors drill-down per supplier.
 *
 * The accountant's go-to screen for supplier calls.  Each supplier card:
 *   - Aggregate stats (count / oldest age / over-due value)
 *   - Aging buckets (0-7 / 8-14 / 15-30 / 30+)
 *   - Expandable bill-by-bill table with DN sub-rows
 *
 * Filter by supplier or view all (sorted by net-outstanding desc — biggest
 * debts first, where attention is needed).
 */
import { useState, useMemo } from 'react'
import {
  Alert, Card, Col, DatePicker, Empty, Input, Row, Select, Space, Spin,
  Statistic, Table, Tag, Typography,
} from 'antd'
import {
  AccountBookOutlined, AlertOutlined, FileSearchOutlined, UserOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const NUM = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

interface DN {
  voucher_id: string
  voucher_no: string
  voucher_date: string
  dn_type: string
  taxable: string
  cgst: string
  sgst: string
  igst: string
  total: string
}
interface Bill {
  pve_id: string
  voucher_id: string
  voucher_no: string
  invoice_no: string
  bill_ref_name: string
  invoice_date: string
  due_date: string | null
  age_days: number
  over_due_days: number
  aging_bucket: string
  grn_no: string | null
  po_no: string | null
  taxable_value: string
  gst_amount: string
  tds_amount: string
  total_payable: string
  dn_total: string
  net_outstanding: string
  dns: DN[]
  notes: string | null
}
interface SupplierSummary {
  supplier_id: string | null
  supplier_name: string
  supplier_gstin: string | null
  bill_count: number
  oldest_bill_age_days: number
  over_due_count: number
  over_due_value: string
  bucket_count: Record<string, number>
  bucket_value: Record<string, string>
  total_payable: string
  total_dn: string
  net_outstanding: string
  bills: Bill[]
}
interface Statement {
  plant_id: string
  as_of: string
  supplier_filter: string | null
  grand_total_payable: string
  grand_total_dn: string
  grand_net_outstanding: string
  over_due_total_value: string
  over_due_total_count: number
  suppliers: SupplierSummary[]
}

const fmt = (v: string | number | null | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN',
    { minimumFractionDigits: dp, maximumFractionDigits: dp })

const bucketColor = (b: string) =>
  b === '30+' ? '#cf1322' : b === '15-30' ? '#fa8c16' : b === '8-14' ? '#1677ff' : '#52c41a'

// ─────────────────────────────────────────────────────────────────────────
export default function BillWisePage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [supplierSearch, setSupplierSearch] = useState('')
  const [asOf, setAsOf] = useState<Dayjs | null>(null)

  const { data, isLoading } = useQuery<Statement>({
    queryKey: ['bill-wise', plantId, asOf?.format('YYYY-MM-DD')],
    queryFn: () => apiClient.get('/accounting/bill-wise-outstanding', {
      params: {
        plant_id: plantId,
        as_of: asOf?.format('YYYY-MM-DD'),
      },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  // Client-side filter (supplier name / GSTIN substring)
  const filtered = useMemo(() => {
    if (!data) return null
    if (!supplierSearch) return data.suppliers
    const q = supplierSearch.toLowerCase()
    return data.suppliers.filter(s =>
      s.supplier_name.toLowerCase().includes(q) ||
      (s.supplier_gstin ?? '').toLowerCase().includes(q),
    )
  }, [data, supplierSearch])

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <AccountBookOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Bill-wise Outstanding
      </Title>
      <Text type="secondary">
        Sundry Creditors drill-down per supplier — open bills with DNs netted,
        aging buckets, and over-due flags. Click a supplier to expand.
      </Text>

      {/* Filters */}
      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Select value={plantId} onChange={setPlantId}
              style={{ width: '100%' }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col xs={24} md={8}>
            <Input.Search
              placeholder="Filter by supplier name or GSTIN…"
              allowClear
              value={supplierSearch}
              onChange={e => setSupplierSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} md={6}>
            <DatePicker
              value={asOf}
              onChange={setAsOf}
              placeholder="As of (today by default)"
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Grand totals */}
      {data && (
        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small" styles={{ body: { padding: 12 } }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Net outstanding</Text>
              <div style={{ ...NUM, fontSize: 22, fontWeight: 700 }}>
                ₹{fmt(data.grand_net_outstanding, 0)}
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" styles={{ body: { padding: 12 } }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Total payable (gross)</Text>
              <div style={{ ...NUM, fontSize: 18, fontWeight: 600 }}>
                ₹{fmt(data.grand_total_payable, 0)}
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" styles={{ body: { padding: 12 } }}>
              <Text type="secondary" style={{ fontSize: 11 }}>− DNs netted</Text>
              <div style={{ ...NUM, fontSize: 18, color: '#cf1322' }}>
                − ₹{fmt(data.grand_total_dn, 0)}
              </div>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small" styles={{ body: { padding: 12 } }}
              style={{ borderColor: data.over_due_total_count > 0 ? '#ffa39e' : undefined,
                       background: data.over_due_total_count > 0 ? '#fff1f0' : undefined }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <AlertOutlined /> Over-due
              </Text>
              <div style={{ ...NUM, fontSize: 18, fontWeight: 700,
                color: data.over_due_total_count > 0 ? '#cf1322' : '#262626' }}>
                {data.over_due_total_count} bills · ₹{fmt(data.over_due_total_value, 0)}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Per-supplier cards */}
      <div style={{ marginTop: 16 }}>
        {isLoading && <Spin />}
        {!isLoading && (!filtered || filtered.length === 0) && (
          <Empty description={supplierSearch
            ? 'No supplier matches the search'
            : 'No outstanding bills — all caught up.'} />
        )}
        {filtered?.map(sup => <SupplierCard key={sup.supplier_id ?? sup.supplier_name} sup={sup} />)}
      </div>
    </div>
  )
}


// ─── One supplier — header + aging tiles + expandable bills table ────────
function SupplierCard({ sup }: { sup: SupplierSummary }) {
  return (
    <Card style={{ marginBottom: 12 }}
      styles={{ body: { padding: 16 } }}>
      <Row align="middle" justify="space-between" gutter={[12, 12]}>
        <Col flex="auto">
          <Space size={12} wrap>
            <UserOutlined style={{ fontSize: 18, color: '#1677ff' }} />
            <div>
              <Text strong style={{ fontSize: 16 }}>{sup.supplier_name}</Text>
              {sup.supplier_gstin && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, fontFamily: 'monospace' }}>
                  {sup.supplier_gstin}
                </Text>
              )}
            </div>
            <Tag>{sup.bill_count} bill(s)</Tag>
            {sup.over_due_count > 0 && (
              <Tag color="error" icon={<AlertOutlined />}>
                {sup.over_due_count} over-due
              </Tag>
            )}
            <Tag color={sup.oldest_bill_age_days > 30 ? 'error'
              : sup.oldest_bill_age_days > 14 ? 'warning' : 'default'}>
              Oldest: {sup.oldest_bill_age_days}d
            </Tag>
          </Space>
        </Col>
        <Col>
          <Text type="secondary" style={{ fontSize: 11 }}>Net outstanding</Text>
          <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: '#cf1322', textAlign: 'right' }}>
            ₹{fmt(sup.net_outstanding, 0)}
          </div>
        </Col>
      </Row>

      {/* Aging tiles */}
      <Row gutter={8} style={{ marginTop: 12 }}>
        {(['0-7', '8-14', '15-30', '30+'] as const).map(b => (
          <Col xs={6} key={b}>
            <Card size="small"
              styles={{ body: { padding: 8 } }}
              style={{
                borderColor: b === '30+' && sup.bucket_count[b] > 0 ? '#ffa39e' : '#f0f0f0',
                background: b === '30+' && sup.bucket_count[b] > 0 ? '#fff1f0' : '#fafafa',
              }}>
              <Text style={{ fontSize: 10, color: bucketColor(b) }}>{b} days</Text>
              <div style={{ ...NUM, fontSize: 14, fontWeight: 600 }}>
                {sup.bucket_count[b]}
              </div>
              <Text type="secondary" style={{ ...NUM, fontSize: 10 }}>
                ₹{fmt(sup.bucket_value[b], 0)}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Per-bill table */}
      <Table size="small"
        style={{ marginTop: 12 }}
        dataSource={sup.bills}
        rowKey="pve_id"
        pagination={sup.bills.length > 10 ? { pageSize: 10 } : false}
        scroll={{ x: 'max-content' }}
        rowClassName={(r) => r.over_due_days > 0 ? 'bw-overdue-row' : ''}
        expandable={{
          expandedRowRender: (r) => <BillDNs bill={r} />,
          rowExpandable: (r) => r.dns.length > 0,
        }}
        columns={[
          { title: 'Bill ref', dataIndex: 'bill_ref_name',
            render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
          { title: 'Date', dataIndex: 'invoice_date',
            render: (v: string) => dayjs(v).format('DD-MMM-YY') },
          { title: 'Due', dataIndex: 'due_date',
            render: (v: string | null, r: Bill) => {
              if (!v) return '—'
              const d = dayjs(v)
              const overdue = r.over_due_days > 0
              return (
                <Text style={{ color: overdue ? '#cf1322' : '#262626', fontSize: 12 }}>
                  {d.format('DD-MMM-YY')}
                  {overdue && <Text type="danger" style={{ marginLeft: 4, fontSize: 10 }}>
                    ({r.over_due_days}d late)
                  </Text>}
                </Text>
              )
            } },
          { title: 'Age', dataIndex: 'age_days', align: 'right' as const,
            render: (v: number, r: Bill) => (
              <Tag color={r.aging_bucket === '30+' ? 'error'
                : r.aging_bucket === '15-30' ? 'warning' : 'default'}>
                {v}d
              </Tag>
            ) },
          { title: 'GRN', dataIndex: 'grn_no',
            render: (v: string | null) => v
              ? <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text>
              : '—' },
          { title: 'Gross ₹', dataIndex: 'total_payable', align: 'right' as const,
            render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
          { title: '− DN ₹', dataIndex: 'dn_total', align: 'right' as const,
            render: (v: string) => Number(v) > 0
              ? <Text style={{ ...NUM, color: '#cf1322' }}>− ₹{fmt(v, 0)}</Text>
              : <Text type="secondary">—</Text> },
          { title: 'Net Payable ₹', dataIndex: 'net_outstanding', align: 'right' as const,
            render: (v: string) => <Text strong style={{ ...NUM, color: '#52c41a' }}>
              ₹{fmt(v, 0)}
            </Text> },
        ]}
      />
    </Card>
  )
}


// ─── Per-bill DN sub-table (expanded row) ────────────────────────────────
function BillDNs({ bill }: { bill: Bill }) {
  return (
    <div style={{ background: '#fffbe6', padding: 8, borderRadius: 4 }}>
      <Text strong style={{ fontSize: 12 }}>
        <FileSearchOutlined /> &nbsp;{bill.dns.length} debit note(s) raised against {bill.bill_ref_name}
      </Text>
      <Table size="small" pagination={false}
        style={{ marginTop: 8 }}
        dataSource={bill.dns}
        rowKey="voucher_id"
        columns={[
          { title: 'DN No.', dataIndex: 'voucher_no',
            render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
          { title: 'Date', dataIndex: 'voucher_date',
            render: (v: string) => dayjs(v).format('DD-MMM-YY') },
          { title: 'Type', dataIndex: 'dn_type',
            render: (v: string) => <Tag color={
              v === 'WEIGHT' ? 'blue' : v === 'QUALITY' ? 'orange' : v === 'RATE' ? 'purple' : 'default'
            }>{v}</Tag> },
          { title: 'Taxable ₹', dataIndex: 'taxable', align: 'right' as const,
            render: (v: string) => <span style={NUM}>₹{fmt(v, 2)}</span> },
          { title: 'CGST ₹', dataIndex: 'cgst', align: 'right' as const,
            render: (v: string) => Number(v) > 0 ? <span style={NUM}>₹{fmt(v, 2)}</span> : '—' },
          { title: 'SGST ₹', dataIndex: 'sgst', align: 'right' as const,
            render: (v: string) => Number(v) > 0 ? <span style={NUM}>₹{fmt(v, 2)}</span> : '—' },
          { title: 'IGST ₹', dataIndex: 'igst', align: 'right' as const,
            render: (v: string) => Number(v) > 0 ? <span style={NUM}>₹{fmt(v, 2)}</span> : '—' },
          { title: 'Total ₹', dataIndex: 'total', align: 'right' as const,
            render: (v: string) => <Text strong style={{ ...NUM, color: '#cf1322' }}>₹{fmt(v, 2)}</Text> },
        ]}
      />
    </div>
  )
}
